import { useMemo } from 'react'

import {
  Text,
  GreyGrey60,
  GreyGrey80,
  Primary13Regular,
  Primary20SemiBold,
  Flex
} from '@sequencehq/core-components'
import {
  apiPhaseToCalculatePhaseTotalAmountPhase,
  toScaledMoney,
  calculateQuoteTotals,
  isAUsageBasedPrice
} from '@sequencehq/utils'
import { getTotalDisclaimerText } from '@sequencehq/quote-content'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { phasesToQuotePhases } from 'Cube/communication/external/common.api.v1/adapters/common.adapters.out'
import { useStartDate } from 'Cube/domain'

export const ContractValue = () => {
  const ctx = useCubeContext()
  const { startDate } = useStartDate()

  const currency = useMemo(
    () =>
      ctx.queries.rawData.data.common.currency ??
      ctx.queries.rawData.configuration.currency.default,
    [
      ctx.queries.rawData.data.common.currency,
      ctx.queries.rawData.configuration.currency.default
    ]
  )

  const total = useMemo(() => {
    const hasPhaseBlock = ctx.queries.rawData.data.presentation.blocks.some(
      b => b.blockType === 'phases'
    )

    if (!hasPhaseBlock) {
      return 0
    }

    const phases = phasesToQuotePhases(ctx.queries.rawData.data)

    const { totalDiscountedRate } = calculateQuoteTotals({
      startDate,
      phases: apiPhaseToCalculatePhaseTotalAmountPhase(phases),
      currency
    })

    return totalDiscountedRate
  }, [ctx.queries.rawData.data, startDate, currency])

  const hasUsageBasedPrices = useMemo(
    () =>
      Object.values(ctx.queries.rawData.data.prices).some(isAUsageBasedPrice),
    [ctx.queries.rawData.data.prices]
  )

  return (
    <Flex flexDir="column" gap="8px" marginBottom="16px">
      <Text
        {...Primary20SemiBold}
        color={GreyGrey80}
        data-testid="quote-information-inspector-contract-value"
      >
        {toScaledMoney({ currency, value: String(total) })}
      </Text>
      <Text
        {...Primary13Regular}
        color={GreyGrey60}
        data-testid="quote-information-inspector-contract-value-disclaimer"
      >
        {getTotalDisclaimerText({ hasUsageBasedPrices, asFootnote: false })}
      </Text>
    </Flex>
  )
}
